import { useState } from "react";
import { useSearchParams } from "react-router-dom";

const parsePage = (value) => {
  const n = parseInt(value, 10);
  return Number.isInteger(n) && n > 0 ? n : null;
};

/**
 * Mémorise la page courante et le tri d'une page de liste.
 *
 *  - La page est dans l'URL (?page=2) : elle survit au rafraîchissement (F5),
 *    au bouton retour, à la réouverture de l'onglet et au partage du lien.
 *  - Elle est aussi gardée dans sessionStorage : quand on revient sur la liste
 *    depuis un projet ou le menu (URL sans ?page), on retrouve la dernière page.
 *  - Le tri est gardé dans sessionStorage.
 *
 * @param {string} key  identifiant unique de la page (ex: "cuisines")
 * @returns [currentPage, setCurrentPage, sortOrder, setSortOrder]
 */
export function usePersistedPagination(key) {
  const storageKey = `pagination:${key}`;
  const [searchParams, setSearchParams] = useSearchParams();

  const readStored = () => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (raw) {
        const { page, sort } = JSON.parse(raw);
        return { page: parsePage(page) || 1, sort: sort ?? null };
      }
    } catch (e) {}
    return { page: 1, sort: null };
  };

  const writeStored = (patch) => {
    try {
      sessionStorage.setItem(
        storageKey,
        JSON.stringify({ ...readStored(), ...patch })
      );
    } catch (e) {}
  };

  const [sortOrder, setSortState] = useState(() => readStored().sort);

  // L'URL fait foi ; sinon on reprend la dernière page connue
  const currentPage = parsePage(searchParams.get("page")) || readStored().page;

  const setCurrentPage = (page) => {
    writeStored({ page });
    const next = new URLSearchParams(searchParams);
    if (page > 1) next.set("page", String(page));
    else next.delete("page");
    setSearchParams(next, { replace: true });
  };

  const setSortOrder = (sort) => {
    setSortState(sort);
    writeStored({ sort });
  };

  return [currentPage, setCurrentPage, sortOrder, setSortOrder];
}
